import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScannerSection, closeCameraAfterScanFlash, type ScannerSectionHandle } from '../scanner';
import { isFadingQrScanError } from '../scanner/qrScanMessages';
import { QrPlaceholderIcon } from '../scanner/WebcamPlaceholder';
import { performOCR } from '../../services/visionAPI';
import { fetchOrderShelfLocations, fetchPrefixMap } from '../../services/skuShelfIndexAPI';
import { fetchSkusWithNotes } from '../../services/productWarehouseNotesAPI';
import { getLabelByStorageIdPrefix, getLabelStatus } from '../../services/shelfIndexAPI';
import {
  changeInventoryTransferQuantity,
  completeInventoryTransfer,
  fetchInventoryTransfer,
  printInventoryTransferStoreLabels,
  recordInventoryTransferScan,
  substituteInventoryTransferSku,
} from '../../services/inventoryTransferAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { InventoryTransferDetail, InventoryTransferLineEvent } from '../../schemas/apiResponse.schemas';
import type { TransferLineChangeBroadcast } from '@/shared/utils/inventoryTransferScan';
import type { OrderItem } from '../../types/apiTypes';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { useHybridShelfIndexSync } from '../../hooks/useHybridShelfIndexSync';
import { useOrderDetailScanMode } from '../../hooks/useOrderDetailScanMode';
import { useTransferScanRealtime } from '../../hooks/useTransferScanRealtime';
import ProductInfoModal from '../shared/ProductInfoModal';
import {
  handleCapture as handleCaptureCore,
  handleQrCapture as handleQrCaptureCore,
  targetedScanFromPickItem,
  type TargetedScan,
} from '../orders/orderdetail/scanningLogic';
import OrderItemsList from '../orders/orderdetail/OrderItemsList';
import {
  CameraIcon,
  PICK_SCAN_BAR_VARIANTS,
  type PickScanBarVariant,
} from '../orders/orderdetail/PickScanBar';
import OrderDetailCameraBar from '../orders/orderdetail/OrderDetailCameraBar';
import { useAutoOpenOrderDetailsCamera } from '../orders/orderdetail/useAutoOpenOrderDetailsCamera';
import { usePickViewTouchLock } from '../orders/orderdetail/usePickViewTouchLock';
import useVisibleItems from '../orders/orderdetail/useVisibleItems';
import {
  applyShelfMapToOrderItems,
  applyShelfLocationToOrderItem,
  getShelfLookupSkus,
  loadOrderShelfLocationMap,
} from '../orders/orderdetail/shelfUtils';
import InventoryTransferNavbar from './InventoryTransferNavbar';
import InventoryTransferChangeModal from './InventoryTransferChangeModal';
import '../orders/orderdetail/OrderDetail.css';
import './InventoryTransfersList.css';

function isSuccessMessage(msg: string): boolean {
  return msg.startsWith('SKU ') && msg.includes('matched');
}

function isStatusMessage(msg: string): boolean {
  return msg.includes('Processing image') || msg.includes('Looking up label');
}

function orderDetailMessageClass(msg: string): string {
  if (isSuccessMessage(msg)) return 'order-detail-message order-detail-message-success';
  if (isStatusMessage(msg)) return 'order-detail-message order-detail-message-default';
  if (isFadingQrScanError(msg)) return 'order-detail-message order-detail-message-error webcam-qr-miss-fade';
  return 'order-detail-message order-detail-message-error';
}

function toPickItems(
  request: InventoryTransferDetail,
): OrderItem[] {
  const scanned = new Set(request.scannedLineIds);
  return request.lines.map((line) => ({
    id: line.id,
    sku: line.sku,
    name: line.displayName,
    scanned: scanned.has(line.id),
    quantity: line.quantity,
    isAdjustedQty: true,
  }));
}

function InventoryTransferDetail(): React.ReactElement {
  const navigate = useNavigate();
  const { transferId = '' } = useParams();
  const { settings } = useUserSettings();
  const { qrActive, toggleScanMode } = useOrderDetailScanMode();
  const { getVisibleItems, observeItem } = useVisibleItems();

  const [request, setRequest] = useState<InventoryTransferDetail | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [webcamOn, setWebcamOn] = useState(false);
  const componentsOnlyMode = true;
  const [prefixToSkuMap, setPrefixToSkuMap] = useState<Record<string, string>>({});
  const [skusWithNotes, setSkusWithNotes] = useState<Record<string, true>>({});
  const [selectedProductInfo, setSelectedProductInfo] = useState<OrderItem | null>(null);
  const [completing, setCompleting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savesInFlight, setSavesInFlight] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [modifyLineId, setModifyLineId] = useState<string | null>(null);
  const [changeSaving, setChangeSaving] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [printingLabels, setPrintingLabels] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const printingLabelsRef = useRef(false);

  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const shelfScanTargetRef = useRef<TargetedScan | null>(null);
  const itemsRef = useRef<OrderItem[]>([]);
  const successFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successNavigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successOverlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appliedLineEventIdsRef = useRef(new Set<string>());
  const knownLineIdsRef = useRef(new Set<string>());
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  itemsRef.current = orderItems;
  const needsShelfRetryRef = useRef(false);

  const title = request
    ? `#${request.transferNumber} ${request.name}`
    : 'Inventory Transfer';
  usePageTitle(title);
  usePickViewTouchLock();

  const allPickLinesScanned = orderItems.length > 0 && orderItems.every((item) => item.scanned);
  const isReadyForSubmit = allPickLinesScanned && !isComplete && savesInFlight === 0;

  useEffect(() => {
    if (!isSuccessMessage(message) && !isFadingQrScanError(message)) return undefined;
    successFadeTimerRef.current = setTimeout(() => setMessage(''), 4000);
    return () => {
      if (successFadeTimerRef.current) clearTimeout(successFadeTimerRef.current);
    };
  }, [message]);

  useEffect(() => {
    if (!allPickLinesScanned) return undefined;
    const timer = closeCameraAfterScanFlash(setWebcamOn);
    return () => clearTimeout(timer);
  }, [allPickLinesScanned]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRequest(null);
    setOrderItems([]);
    setIsComplete(false);
    setCompleting(false);
    setShowSuccess(false);
    setSavesInFlight(0);
    setModifyLineId(null);
    setChangeSaving(false);
    setChangeError(null);
    printingLabelsRef.current = false;
    setPrintingLabels(false);
    setPrintError(null);
    appliedLineEventIdsRef.current = new Set();
    knownLineIdsRef.current = new Set();
    if (successNavigateTimerRef.current) {
      clearTimeout(successNavigateTimerRef.current);
      successNavigateTimerRef.current = null;
    }
    setMessage('');
    setWebcamOn(false);
    setSelectedProductInfo(null);

    const load = async (): Promise<void> => {
      try {
        const next = await fetchInventoryTransfer(transferId);
        if (cancelled) return;
        const baseItems = toPickItems(next);
        const uniqueSkus = getShelfLookupSkus(baseItems);
        const [shelfResult, prefixMap, notesMap] = await Promise.all([
          loadOrderShelfLocationMap(uniqueSkus, fetchOrderShelfLocations),
          fetchPrefixMap(uniqueSkus),
          fetchSkusWithNotes(uniqueSkus),
        ]);
        if (cancelled) return;
        appliedLineEventIdsRef.current = new Set(next.lineEvents.map((event) => event.id));
        knownLineIdsRef.current = new Set([
          ...next.lines.map((line) => line.id),
          ...next.lineEvents.map((event) => event.lineId),
        ]);
        setRequest(next);
        setIsComplete(next.completedAt != null);
        setPrefixToSkuMap(prefixMap);
        setSkusWithNotes(notesMap);
        setOrderItems(applyShelfMapToOrderItems(baseItems, shelfResult.map, shelfResult.loaded));
        if (!shelfResult.loaded) {
          needsShelfRetryRef.current = true;
        }
      } catch (err: unknown) {
        if (cancelled) return;
        setLoadError(getApiErrorMessage(err, 'Failed to load inventory transfer'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (successNavigateTimerRef.current) {
        clearTimeout(successNavigateTimerRef.current);
        successNavigateTimerRef.current = null;
      }
      if (successOverlayTimerRef.current) {
        clearTimeout(successOverlayTimerRef.current);
        successOverlayTimerRef.current = null;
      }
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
  }, [transferId]);

  const refreshShelfLocations = useCallback(async () => {
    const uniqueSkus = getShelfLookupSkus(itemsRef.current);
    if (uniqueSkus.length === 0) return;
    let shelfLocationsMap: Awaited<ReturnType<typeof fetchOrderShelfLocations>>;
    let notesMap: Awaited<ReturnType<typeof fetchSkusWithNotes>>;
    try {
      [shelfLocationsMap, notesMap] = await Promise.all([
        fetchOrderShelfLocations(uniqueSkus),
        fetchSkusWithNotes(uniqueSkus),
      ]);
    } catch (error: unknown) {
      console.error('Error refreshing inventory transfer shelf locations:', error);
      return;
    }
    setSkusWithNotes(notesMap);
    setOrderItems((prevItems) => {
      let hasChanges = false;
      const nextItems = prevItems.map((item) => {
        const loc = shelfLocationsMap[item.sku.toUpperCase()] || { shelfIndex: null, type: null };
        const nextItem = applyShelfLocationToOrderItem(item, loc);
        if (nextItem.shelfIndex !== item.shelfIndex) hasChanges = true;
        return nextItem;
      });
      return hasChanges ? nextItems : prevItems;
    });
  }, []);

  useEffect(() => {
    if (!needsShelfRetryRef.current || loading || orderItems.length === 0) {
      return;
    }
    needsShelfRetryRef.current = false;
    void refreshShelfLocations();
  }, [loading, orderItems.length, refreshShelfLocations]);

  useHybridShelfIndexSync({
    enabled: Boolean(transferId) && !loading && orderItems.length > 0,
    onSync: refreshShelfLocations,
    pollMs: 20000,
    tables: ['shelf_indexes', 'product_warehouse_notes'],
  });

  useAutoOpenOrderDetailsCamera({
    sessionKey: transferId,
    loading,
    enabled: settings.cameraOpensOrderDetails && !isComplete,
    itemCount: orderItems.length,
    allPickLinesScanned,
    isSubmitted: isComplete,
    setWebcamOn,
  });

  const markLineScanned = useCallback((lineId: string) => {
    setOrderItems((prev) => {
      if (!prev.some((item) => item.id === lineId && !item.scanned)) return prev;
      return prev.map((item) => (
        item.id === lineId ? { ...item, scanned: true, scanPulse: Date.now() } : item
      ));
    });
  }, []);

  const applyLineChange = useCallback((change: TransferLineChangeBroadcast) => {
    if (change.transferRequestId !== transferId) return;
    const eventType = change.event.eventType === 'sku_substituted'
      ? 'sku_substituted'
      : change.event.eventType === 'quantity_changed'
        ? 'quantity_changed'
        : null;
    if (!eventType) return;
    if (appliedLineEventIdsRef.current.has(change.eventId)) return;
    appliedLineEventIdsRef.current.add(change.eventId);

    const event: InventoryTransferLineEvent = {
      id: change.event.id,
      lineId: change.event.lineId,
      eventType,
      sku: change.event.sku,
      previousSku: change.event.previousSku,
      newSku: change.event.newSku,
      previousQuantity: change.event.previousQuantity,
      newQuantity: change.event.newQuantity,
      actorName: change.event.actorName,
      createdAt: change.event.createdAt,
    };
    const nextLine = change.line;

    setRequest((prev) => {
      if (!prev) return prev;
      const lineEvents = prev.lineEvents.some((row) => row.id === event.id)
        ? prev.lineEvents
        : [...prev.lineEvents, event];
      const lines = nextLine
        ? (prev.lines.some((row) => row.id === change.lineId)
          ? prev.lines.map((row) => (row.id === change.lineId
            ? {
              ...nextLine,
              id: change.lineId,
              requestedQuantity: nextLine.requestedQuantity ?? row.requestedQuantity,
            }
            : row))
          : [...prev.lines, {
            ...nextLine,
            id: change.lineId,
            requestedQuantity: nextLine.requestedQuantity ?? nextLine.quantity,
          }])
        : prev.lines.filter((row) => row.id !== change.lineId);
      return { ...prev, lineEvents, lines };
    });
    setOrderItems((prev) => {
      const existing = prev.find((item) => item.id === change.lineId);
      let next: OrderItem[];
      if (!nextLine) {
        next = prev.filter((item) => item.id !== change.lineId);
      } else if (existing) {
        next = prev.map((item) => (item.id === change.lineId
          ? {
            ...item,
            sku: nextLine.sku,
            name: nextLine.displayName,
            quantity: nextLine.quantity,
            isAdjustedQty: true,
          }
          : item));
      } else {
        next = [...prev, {
          id: change.lineId,
          sku: nextLine.sku,
          name: nextLine.displayName,
          quantity: nextLine.quantity,
          scanned: false,
          isAdjustedQty: true,
        }];
      }
      itemsRef.current = next;
      return next;
    });
    knownLineIdsRef.current.add(change.lineId);
    if (!nextLine) {
      setModifyLineId((current) => (current === change.lineId ? null : current));
    }
    void refreshShelfLocations();
  }, [refreshShelfLocations, transferId]);

  const reloadPickList = useCallback(async (): Promise<void> => {
    if (!transferId) return;
    try {
      const next = await fetchInventoryTransfer(transferId);
      for (const event of next.lineEvents) appliedLineEventIdsRef.current.add(event.id);
      knownLineIdsRef.current = new Set([
        ...knownLineIdsRef.current,
        ...next.lines.map((line) => line.id),
        ...next.lineEvents.map((event) => event.lineId),
      ]);
      setRequest(next);
      setIsComplete(next.completedAt != null);
      setOrderItems((prev) => {
        const priorById = new Map(prev.map((item) => [item.id, item]));
        const nextItems = toPickItems(next).map((item) => {
          const prior = priorById.get(item.id);
          if (!prior) return item;
          return {
            ...item,
            scanned: prior.scanned,
            shelfIndex: prior.shelfIndex,
            shelfDisplay: prior.shelfDisplay,
            scanPulse: prior.scanPulse,
          };
        });
        itemsRef.current = nextItems;
        return nextItems;
      });
      void refreshShelfLocations();
    } catch {
      // The open pick list stays as it is until the next event.
    }
  }, [refreshShelfLocations, transferId]);

  const handleRemoteLineEvent = useCallback((event: { readonly lineId: string; readonly eventId: string }) => {
    if (!knownLineIdsRef.current.has(event.lineId)) return;
    if (appliedLineEventIdsRef.current.has(event.eventId)) return;
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      void reloadPickList();
    }, 200);
  }, [reloadPickList]);

  useTransferScanRealtime({
    transferRequestId: transferId,
    enabled: !loading && !isComplete && request != null,
    onScan: markLineScanned,
    onLineChange: applyLineChange,
    onLineEvent: handleRemoteLineEvent,
  });

  const persistNewScans = useCallback(async (before: readonly OrderItem[], after: readonly OrderItem[]) => {
    const already = new Set(before.filter((item) => item.scanned).map((item) => item.id));
    const newly = after.filter((item) => item.scanned && !already.has(item.id));
    if (newly.length === 0) return;
    setSavesInFlight((count) => count + newly.length);
    try {
      for (const item of newly) {
        try {
          await recordInventoryTransferScan(transferId, item.id);
        } catch (err: unknown) {
          setOrderItems((prev) => prev.map((row) => (
            row.id === item.id ? { ...row, scanned: false } : row
          )));
          setMessage(getApiErrorMessage(err, 'Failed to save scan'));
        }
      }
    } finally {
      setSavesInFlight((count) => count - newly.length);
    }
  }, [transferId]);

  const applyScanResult = useCallback((before: readonly OrderItem[]) => (next: OrderItem[]) => {
    setOrderItems((current) => {
      const scannedIds = new Set(current.filter((item) => item.scanned).map((item) => item.id));
      if (scannedIds.size === 0) return next;
      return next.map((item) => (
        scannedIds.has(item.id) ? { ...item, scanned: true } : item
      ));
    });
    void persistNewScans(before, next);
  }, [persistNewScans]);

  const consumeShelfScanTarget = (): TargetedScan | undefined => {
    const target = shelfScanTargetRef.current ?? undefined;
    shelfScanTargetRef.current = null;
    return target;
  };

  const handleSelectShelf = (item: OrderItem): void => {
    if (!webcamOn || isComplete) return;
    const target = targetedScanFromPickItem(item);
    if (!target) return;
    shelfScanTargetRef.current = target;
    scannerCaptureRef.current?.capture();
  };

  const handleCapture = async (imageSrc: string | null): Promise<boolean> => {
    const target = consumeShelfScanTarget();
    if (!imageSrc || !transferId || isComplete) return false;
    const before = itemsRef.current;
    const scanResult = await handleCaptureCore(
      imageSrc,
      performOCR,
      before,
      applyScanResult(before),
      setMessage,
      getVisibleItems(),
      componentsOnlyMode,
      new Map(),
      undefined,
      target,
    );
    return scanResult.matched;
  };

  const handleQrCapture = async (qrData: string | null): Promise<boolean> => {
    const target = consumeShelfScanTarget();
    if (!qrData || !transferId || isComplete) return false;
    const before = itemsRef.current;
    const scanResult = await handleQrCaptureCore(
      qrData,
      prefixToSkuMap,
      getLabelStatus,
      getLabelByStorageIdPrefix,
      before,
      applyScanResult(before),
      setMessage,
      getVisibleItems(),
      componentsOnlyMode,
      new Map(),
      undefined,
      target,
    );
    return scanResult.matched;
  };

  const flashSuccess = useCallback(() => {
    setShowSuccess(true);
    if (successOverlayTimerRef.current) clearTimeout(successOverlayTimerRef.current);
    successOverlayTimerRef.current = setTimeout(() => setShowSuccess(false), 1000);
  }, []);

  const handleChangeQuantity = async (quantity: number): Promise<void> => {
    if (!transferId || !modifyLineId) return;
    setChangeError(null);
    setChangeSaving(true);
    try {
      const result = await changeInventoryTransferQuantity(transferId, modifyLineId, quantity);
      applyLineChange({
        transferRequestId: transferId,
        lineId: result.event.lineId,
        eventId: result.event.id,
        line: result.line,
        event: result.event,
      });
      flashSuccess();
    } catch (err: unknown) {
      setChangeError(getApiErrorMessage(err, 'Failed to update quantity'));
      throw err;
    } finally {
      setChangeSaving(false);
    }
  };

  const handleSubstitute = async (sku: string, note?: string): Promise<void> => {
    if (!transferId || !modifyLineId) return;
    setChangeError(null);
    setChangeSaving(true);
    try {
      const result = await substituteInventoryTransferSku(transferId, modifyLineId, sku, note);
      const updates = [result, ...result.merged];
      for (const update of updates) {
        applyLineChange({
          transferRequestId: transferId,
          lineId: update.event.lineId,
          eventId: update.event.id,
          line: update.line,
          event: update.event,
        });
      }
      flashSuccess();
    } catch (err: unknown) {
      setChangeError(getApiErrorMessage(err, 'Failed to substitute SKU'));
      throw err;
    } finally {
      setChangeSaving(false);
    }
  };

  const handlePrintStoreLabels = async (): Promise<void> => {
    if (!transferId || !modifyLineId || printingLabelsRef.current) return;
    printingLabelsRef.current = true;
    setPrintError(null);
    setPrintingLabels(true);
    try {
      await printInventoryTransferStoreLabels(transferId, modifyLineId);
      flashSuccess();
    } catch (err: unknown) {
      setPrintError(getApiErrorMessage(err, 'Failed to print store labels'));
    } finally {
      printingLabelsRef.current = false;
      setPrintingLabels(false);
    }
  };

  const handleComplete = async (): Promise<void> => {
    if (!transferId || completing || !isReadyForSubmit) return;
    setCompleting(true);
    try {
      await completeInventoryTransfer(transferId);
      if (successOverlayTimerRef.current) {
        clearTimeout(successOverlayTimerRef.current);
        successOverlayTimerRef.current = null;
      }
      setShowSuccess(true);
      successNavigateTimerRef.current = setTimeout(() => {
        navigate('/inventory-transfers');
      }, 1000);
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err, 'Failed to complete transfer'));
      setCompleting(false);
    }
  };

  const viewportMessageOverlay = webcamOn && message ? (
    <div className={orderDetailMessageClass(message)} role="status">
      {message}
    </div>
  ) : null;

  const barVariant: PickScanBarVariant = isComplete
    ? PICK_SCAN_BAR_VARIANTS.NAV
    : isReadyForSubmit
      ? PICK_SCAN_BAR_VARIANTS.SUBMIT
      : PICK_SCAN_BAR_VARIANTS.CAMERA;

  const cameraLeftActions = (
    <div className="order-detail-bottom-bar-left-group">
      <button
        type="button"
        className="order-detail-scan-mode-toggle"
        onClick={toggleScanMode}
        aria-pressed={qrActive}
        title={qrActive ? 'Switch to text (OCR) scan' : 'Switch to QR code scan'}
        aria-label={qrActive ? 'Scan mode: QR. Switch to text.' : 'Scan mode: text. Switch to QR.'}
      >
        <span className={`order-detail-scan-mode-option${qrActive ? ' active' : ''}`}>
          <QrPlaceholderIcon />
        </span>
        <span className={`order-detail-scan-mode-option${!qrActive ? ' active' : ''}`}>
          <span className="placeholder-text-chip">a..z</span>
        </span>
      </button>
    </div>
  );

  const navLeftActions = (
    <button
      type="button"
      onClick={() => navigate('/inventory-transfers')}
      className="order-detail-bottom-bar-nav-btn"
    >
      All Transfers
    </button>
  );

  const leftActions = barVariant === PICK_SCAN_BAR_VARIANTS.NAV
    ? navLeftActions
    : barVariant === PICK_SCAN_BAR_VARIANTS.CAMERA
      ? cameraLeftActions
      : undefined;

  const centerActions = barVariant === PICK_SCAN_BAR_VARIANTS.CAMERA ? (
    <button
      type="button"
      className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
      onClick={() => setWebcamOn(true)}
      aria-label="Open camera"
    >
      <CameraIcon size={28} />
    </button>
  ) : barVariant === PICK_SCAN_BAR_VARIANTS.SUBMIT ? (
    <button
      type="button"
      onClick={() => { void handleComplete(); }}
      disabled={!isReadyForSubmit || completing}
      className={`order-detail-submit-btn ${isReadyForSubmit && !completing ? 'active' : ''}`}
    >
      {completing ? 'Completing…' : 'Complete Transfer'}
    </button>
  ) : undefined;

  const modifyLine = request?.lines.find((line) => line.id === modifyLineId) ?? null;
  const modifyEvents = request?.lineEvents ?? [];

  const navbar = (
    <InventoryTransferNavbar
      transferNumber={request ? String(request.transferNumber) : ''}
      transferName={request?.name ?? ''}
      routeLabel={request ? `${request.startLocationName} → ${request.endLocationName}` : ''}
    />
  );

  if (loading) {
    return (
      <div className="order-detail-container">
        {navbar}
        <h1>Loading...</h1>
        <OrderDetailCameraBar open={false} />
      </div>
    );
  }

  return (
    <div className={`order-detail-container${webcamOn ? ' order-detail-camera-open' : ''}`}>
      {showSuccess ? (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      ) : null}
      {navbar}
      {loadError ? <p className="inventory-transfer-error">{loadError}</p> : null}
      {message && !webcamOn ? (
        <div className="order-detail-flash" aria-live="polite">
          <div className={orderDetailMessageClass(message)}>{message}</div>
        </div>
      ) : null}
      {!loadError && orderItems.length === 0 ? (
        <p className="inventory-transfer-empty-lines">This transfer has no SKUs yet.</p>
      ) : null}
      <OrderItemsList
        orderItems={orderItems}
        pickListComponentsOnly={componentsOnlyMode}
        skusWithNotes={skusWithNotes}
        observeItem={observeItem}
        onSelectItem={setSelectedProductInfo}
        onSelectShelf={handleSelectShelf}
        shelfScanEnabled={webcamOn && !isComplete}
        onModify={isComplete ? undefined : (item) => {
          setChangeError(null);
          setPrintError(null);
          setModifyLineId(item.firstItemId ?? item.id);
        }}
      />
      {selectedProductInfo ? (
        <ProductInfoModal
          item={selectedProductInfo}
          onClose={() => setSelectedProductInfo(null)}
        />
      ) : null}
      {modifyLine ? (
        <InventoryTransferChangeModal
          line={modifyLine}
          lines={request?.lines ?? []}
          events={modifyEvents}
          saving={changeSaving}
          error={changeError}
          onClose={() => {
            setModifyLineId(null);
            setChangeError(null);
            setPrintError(null);
          }}
          onChangeQuantity={handleChangeQuantity}
          onSubstitute={handleSubstitute}
          printing={printingLabels}
          printError={printError}
          onPrintStoreLabels={handlePrintStoreLabels}
        />
      ) : null}
      <OrderDetailCameraBar
        open={webcamOn}
        onShutter={() => scannerCaptureRef.current?.capture()}
        barVariant={barVariant}
        leftActions={leftActions}
        centerActions={centerActions}
      >
        <ScannerSection
          ref={scannerCaptureRef}
          mode={qrActive ? 'qr' : 'barcode'}
          onCapture={qrActive ? handleQrCapture : handleCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container"
          overlay={viewportMessageOverlay}
          cornerAction={(
            <button
              type="button"
              className="webcam-viewport-close-btn"
              onClick={(event) => {
                event.stopPropagation();
                setWebcamOn(false);
              }}
              aria-label="Close camera"
            >
              ×
            </button>
          )}
        />
      </OrderDetailCameraBar>
    </div>
  );
}

export default InventoryTransferDetail;
